/**
 * Formatting utilities for consistent UI display
 * Backend still uses exact values - these are for display only
 */

/**
 * Format currency values to whole numbers with commas
 * Example: 1234.56 -> "$1,235"
 * Backend still uses exact values, only UI display changes
 */
export function formatCurrency(value: number): string {
  if (value === null || value === undefined || isNaN(value)) {
    return '$0';
  }
  // Round to whole number and add commas
  return `$${Math.round(Math.abs(value)).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;
}

/**
 * Format numbers with commas for thousands
 * Example: 1234.56 -> "1,235"
 */
export function formatNumber(value: number): string {
  if (value === null || value === undefined || isNaN(value)) {
    return '0';
  }
  // Round to whole number and add commas
  return Math.round(Math.abs(value)).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

/**
 * Format percentage values to one decimal place
 * Example: 19.678 -> "19.7%"
 */
export function formatPercent(value: number): string {
  if (value === null || value === undefined || isNaN(value)) {
    return '0.0%';
  }
  return `${value.toFixed(1)}%`;
}

/**
 * Standardize time period format to match Sales tab
 * Examples: "weekly" -> "Weekly", "last_30_days" -> "Last 30 Days"
 */
export function formatTimePeriod(period: string): string {
  if (!period) return '';
  
  // Handle common period formats
  const periodMap: Record<string, string> = {
    'daily': 'Daily',
    'weekly': 'Weekly',
    'bi_weekly': 'Bi-Weekly',
    'semi_monthly': 'Semi-Monthly',
    'monthly': 'Monthly',
    'quarterly': 'Quarterly',
    'yearly': 'Yearly',
    'last_7_days': 'Last 7 Days',
    'last_30_days': 'Last 30 Days',
    'last_90_days': 'Last 90 Days',
    'last_month': 'Last Month',
    'last_quarter': 'Last Quarter',
    'last_year': 'Last Year',
    'this_month': 'This Month',
    'this_quarter': 'This Quarter',
    'this_year': 'This Year',
    'ytd': 'Year to Date',
    'mtd': 'Month to Date',
    'qtd': 'Quarter to Date',
  };
  
  // Check if we have a mapped value
  const lowerPeriod = period.toLowerCase();
  if (periodMap[lowerPeriod]) {
    return periodMap[lowerPeriod];
  }
  
  // If not found, capitalize first letter of each word
  return period
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}
